import type { HealthCheckResult } from '@solar/contracts';

import { webConfig } from '../config/env';
import { useReadiness } from '../hooks/useReadiness';
import { ApiError } from '../lib/api-error';
import { cx } from '../lib/class-names';

import styles from './StatusPage.module.css';

const STATUS_MODIFIERS: Record<HealthCheckResult['status'], string | undefined> = {
  ok: styles.badgeOk,
  degraded: styles.badgeWarn,
  error: styles.badgeError,
};

function statusClassName(status: HealthCheckResult['status']): string {
  return cx(styles.badge, STATUS_MODIFIERS[status]);
}

/**
 * Demonstrates end-to-end connectivity between this application and the API.
 *
 * Kept for developers and operators; it is not part of the customer journey.
 */
export function StatusPage() {
  const { data, error, isPending, isFetching, refetch } = useReadiness();

  return (
    <div>
      <h1>API status</h1>
      <p className={styles.endpoint}>
        Checking <code>{webConfig.apiBaseUrl}/health/ready</code>
      </p>

      <button
        type="button"
        className={styles.refreshButton}
        onClick={() => void refetch()}
        disabled={isFetching}
      >
        {isFetching ? 'Checking...' : 'Check again'}
      </button>

      {/* Announced politely so a screen reader hears the outcome without
          interrupting whatever the user is doing. */}
      <div aria-live="polite" aria-busy={isPending} className={styles.results}>
        {isPending ? <p>Contacting the API...</p> : null}

        {error !== null && !isPending ? (
          <div className={styles.errorPanel} role="alert">
            <h2 className={styles.errorTitle}>The API could not be reached</h2>
            <p>{error.message}</p>
            {error instanceof ApiError && error.requestId !== undefined ? (
              <p className={styles.requestId}>
                Reference: <code>{error.requestId}</code>
              </p>
            ) : null}
          </div>
        ) : null}

        {data !== undefined ? (
          <div className={styles.panel}>
            <p>
              Overall status: <span className={statusClassName(data.status)}>{data.status}</span>
            </p>

            <table className={styles.table}>
              <caption className="visually-hidden">API dependency check results</caption>
              <thead>
                <tr>
                  <th scope="col">Dependency</th>
                  <th scope="col">Status</th>
                  <th scope="col">Response time</th>
                </tr>
              </thead>
              <tbody>
                {data.checks.map((check) => (
                  <tr key={check.name}>
                    <th scope="row">{check.name}</th>
                    <td>
                      <span className={statusClassName(check.status)}>{check.status}</span>
                    </td>
                    <td>{check.durationMs} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <dl className={styles.metaList}>
              <dt>API version</dt>
              <dd>{data.version}</dd>
              <dt>Uptime</dt>
              <dd>{data.uptimeSeconds} seconds</dd>
              <dt>Checked at</dt>
              <dd>{data.timestamp}</dd>
            </dl>
          </div>
        ) : null}
      </div>
    </div>
  );
}
